/**
 * The bytes a Standard MIDI File is made of.
 *
 * A MIDI file is not a text format and there is no reader for it in a browser, so this is
 * the whole of one: chunk headers, big-endian integers, and the variable-length quantity
 * that delta times and meta lengths are written in. Nothing here knows what a note is --
 * it is the same layer `xml/` is for MusicXML, and it is separate for the same reason: the
 * parts that can be got wrong by one bit are worth reading on their own.
 *
 * A variable-length quantity carries seven bits per byte, high bit set on every byte but
 * the last. Four bytes is the format's limit, which is 0x0FFFFFFF ticks -- about eleven
 * hours at this editor's resolution, and more than a delta time is ever asked to hold.
 */

/** The most a variable-length quantity can carry, which is four seven-bit bytes. */
export const VLQ_MAX = 0x0fffffff;

/** A big-endian 16-bit integer. */
export function u16(value: number): readonly number[] {
  return [(value >> 8) & 0xff, value & 0xff];
}

/** A big-endian 32-bit integer. */
export function u32(value: number): readonly number[] {
  return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
}

/**
 * A variable-length quantity.
 *
 * A value past the format's limit is clamped rather than wrapped: a delta time that wrapped
 * would put a note somewhere else in the piece, which is silently wrong, where a clamped
 * one is merely late.
 */
export function vlq(value: number): readonly number[] {
  const whole = Math.min(Math.max(Math.round(value), 0), VLQ_MAX);
  const bytes = [whole & 0x7f];
  let rest = whole >>> 7;
  while (rest > 0) {
    bytes.unshift((rest & 0x7f) | 0x80);
    rest >>>= 7;
  }
  return bytes;
}

/**
 * Text as bytes, for a track name or a copyright notice.
 *
 * The format predates Unicode and says nothing about it, so a character past ASCII is
 * dropped rather than written as a byte that would be read as another letter.
 */
export function ascii(text: string): readonly number[] {
  const bytes: number[] = [];
  for (const letter of text) {
    const code = letter.codePointAt(0) ?? 0;
    if (code >= 0x20 && code <= 0x7e) bytes.push(code);
  }
  return bytes;
}

/** A chunk: four letters, the length of what follows, then it. */
export function chunk(tag: string, body: readonly number[]): readonly number[] {
  return [...ascii(tag), ...u32(body.length), ...body];
}

export function bytesOf(...parts: readonly (readonly number[])[]): Uint8Array {
  const flat = parts.flat();
  const out = new Uint8Array(flat.length);
  flat.forEach((byte, at) => (out[at] = byte & 0xff));
  return out;
}

/**
 * Where a reader has got to in the file.
 *
 * `at` moves, which is what a reader is; everything else about it is fixed. It is a plain
 * object rather than a class because every function below is the whole of what it can do.
 */
export interface Cursor {
  readonly data: Uint8Array;
  at: number;
}

export function cursorOf(data: Uint8Array): Cursor {
  return { data, at: 0 };
}

/** Whether `count` more bytes can be read without running off the end. */
export function has(cursor: Cursor, count: number): boolean {
  return cursor.at + count <= cursor.data.length;
}

export function readU8(cursor: Cursor): number {
  const byte = cursor.data[cursor.at] ?? 0;
  cursor.at += 1;
  return byte;
}

export function readU16(cursor: Cursor): number {
  return (readU8(cursor) << 8) | readU8(cursor);
}

export function readU32(cursor: Cursor): number {
  return ((readU16(cursor) << 16) >>> 0) + readU16(cursor);
}

/**
 * A variable-length quantity, stopping at four bytes.
 *
 * A file whose high bit never clears would otherwise read to the end of the data as one
 * number; four bytes is the format's own limit and so is where a malformed one is cut.
 */
export function readVlq(cursor: Cursor): number {
  let value = 0;
  for (let taken = 0; taken < 4; taken += 1) {
    const byte = readU8(cursor);
    value = (value << 7) | (byte & 0x7f);
    if ((byte & 0x80) === 0) break;
  }
  return value >>> 0;
}

export function readBytes(cursor: Cursor, count: number): Uint8Array {
  const taken = cursor.data.slice(cursor.at, cursor.at + count);
  cursor.at += count;
  return taken;
}

export function readAscii(cursor: Cursor, count: number): string {
  return Array.from(readBytes(cursor, count), (byte) => String.fromCharCode(byte)).join("");
}
